/**
 * Formatting Helper for Filter Module
 *
 * This service provides comprehensive currency and price formatting utilities for the filter system,
 * following Shopify shop configurations and supporting multiple locale formats. It can be extended
 * by Technical Support teams to implement shop-specific formatting rules, custom currency handling,
 * or specialized price display logic.
 *
 * @remarks
 * Key features:
 * - Currency formatting with locale-specific delimiters and separators
 * - Flexible price display options (decimals, superscript cents, trailing zeros)
 * - Multi-variant price range formatting and validation
 * - Price comparison utilities
 * - HTML price validation
 *
 * All public methods can be overridden to customize formatting behavior for specific shops.
 *
 * @example
 * Extend to add custom currency formatting:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
 *   return class CustomFilterFormatHelper extends FilterFormatHelper {
 *     formatCurrency(options) {
 *       // Add custom rounding for specific currencies
 *       if (this.appService.TAEAppConfig.shop?.currency === 'JPY') {
 *         options.removePriceDecimal = true;
 *       }
 *       return super.formatCurrency(options);
 *     }
 *   };
 * });
 * ```
 */

import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';

@Injectable
export class FilterFormatHelper {
  constructor(private appService: AppService) {}

  /**
   * Formats a currency value according to shop settings.
   *
   * This method applies shop-specific currency formatting using Shopify's money_format
   * or money_format_with_currency configuration. It supports various display options
   * including currency codes, superscript cents, and decimal removal. Override this method
   * to implement custom formatting rules for specific shops or currencies.
   *
   * @param options - Formatting options object
   * @param options.value - The numeric or string value to format. Returns null if undefined/null.
   * @param options.showCurrencyCodes - If true, uses money_format_with_currency (e.g., "$10.00 USD")
   * @param options.showCentAsSuperscript - If true, displays cents as superscript (e.g., "$10<sup>00</sup>")
   * @param options.removeDecimalPoint - If true, removes decimal point from cents in superscript mode
   * @param options.isPriceCompare - Indicates if this is a compare-at price (for styling/logic)
   * @param options.removePriceDecimal - If true, removes entire decimal portion from price
   *
   * @returns Formatted currency string according to shop settings, or null if value is null/undefined
   *
   * @example
   * Basic usage:
   * ```typescript
   * const formatted = helper.formatCurrency({ value: 1999, showCurrencyCodes: true });
   * // Returns: "$19.99 USD" (based on shop configuration)
   * ```
   *
   * @example
   * Extend for custom currency rounding:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     formatCurrency(options) {
   *       // Round JPY to nearest whole number
   *       if (this.appService.TAEAppConfig.shop?.currency === 'JPY') {
   *         options.removePriceDecimal = true;
   *         options.value = Math.round(Number(options.value));
   *       }
   *       return super.formatCurrency(options);
   *     }
   *   };
   * });
   * ```
   */
  formatCurrency(options: {
    value: number | string | null | undefined;
    showCurrencyCodes?: boolean;
    showCentAsSuperscript?: boolean;
    removeDecimalPoint?: boolean;
    isPriceCompare?: boolean;
    removePriceDecimal?: boolean;
  }): string | null {
    const {
      value,
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint,
      removePriceDecimal,
    } = options;

    if (value === null || value === undefined) return null;

    const shop = this.appService.TAEAppConfig.shop || {};
    const formatType = showCurrencyCodes ? shop?.money_format_with_currency : shop?.money_format;

    const formatted = this.formatMoney(
      Number(value),
      formatType,
      false,
      showCentAsSuperscript,
      removePriceDecimal,
      removeDecimalPoint
    );

    return formatted;
  }

  /**
   * Formats money value according to specified format options.
   *
   * This is the core formatting method that applies Shopify money format templates
   * (e.g., "${{amount}}", "{{amount}} USD") to numeric values. It handles various
   * format types including amount, amount_no_decimals, amount_with_comma_separator, etc.
   * Override this method to customize the fundamental money formatting logic.
   *
   * @param money - Money value in cents (e.g., 1999 for $19.99) or string representation
   * @param format - Shopify format template string (e.g., "${{amount}}"). Defaults to shop's money_format.
   * @param withTrailingZeros - If false, removes .00 or ,00 from end of formatted string
   * @param showCentAsSuperscript - If true, displays decimal portion as superscript HTML
   * @param removePriceDecimal - If true, removes entire decimal portion (e.g., $19.99 → $19)
   * @param removeDecimalPoint - If true, removes decimal point when using superscript cents
   *
   * @returns Formatted money string with specified options applied
   *
   * @example
   * Basic usage:
   * ```typescript
   * const formatted = helper.formatMoney(1999, '${{amount}}');
   * // Returns: "$19.99"
   * ```
   *
   * @example
   * Extend to add custom format types:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     formatMoney(money, format, withTrailingZeros, showCentAsSuperscript, removePriceDecimal, removeDecimalPoint) {
   *       // Add support for custom "rounded" format
   *       if (format === 'money_rounded') {
   *         format = '${{amount_no_decimals}}';
   *       }
   *       return super.formatMoney(money, format, withTrailingZeros, showCentAsSuperscript, removePriceDecimal, removeDecimalPoint);
   *     }
   *   };
   * });
   * ```
   */
  formatMoney(
    money: number | string,
    format?: string,
    withTrailingZeros?: boolean,
    showCentAsSuperscript?: boolean,
    removePriceDecimal?: boolean,
    removeDecimalPoint?: boolean
  ): string {
    const shop = this.appService.TAEAppConfig.shop || {};

    if (!format) format = shop?.money_format;
    if (format === 'money_with_currency') format = shop?.money_format_with_currency;

    if (typeof money === 'string') {
      money = money.replace('.', '');
    }

    // Strip HTML from format
    format = this.stripHtml(format || '');

    // Get type format money
    const typeFormatMoney = this.getTypeFormatMoney(format);

    // Format money by type
    let moneyFormat = this.formatMoneyByType(typeFormatMoney, money);

    // Format money with trailing zeros
    if (!withTrailingZeros) moneyFormat = this.replaceDotCommaZero(moneyFormat);

    // Remove decimal
    if (removePriceDecimal) {
      moneyFormat = this.removeDecimal(
        moneyFormat,
        (this.appService.TAEAppConfig?.generalSettings as any).decimalDelimiter
      );
    }

    // Format money with cent as superscript
    if (showCentAsSuperscript) {
      const [ints, cents] = this.separateDecimalMoney(
        moneyFormat,
        typeFormatMoney,
        removeDecimalPoint
      );
      if (cents) moneyFormat = `${ints}<sup>${cents}</sup>`;
    }

    // Replace config format with money format
    return this.compileMoneyWithConfigFormat(moneyFormat, format);
  }

  /**
   * Checks if multi-variant price should be displayed based on translation template.
   *
   * Determines whether a translation template expects a price range format by checking
   * for {{minPrice}} or {{maxPrice}} placeholders. This is used to conditionally display
   * price ranges like "$10.00 - $20.00" versus single prices. Override this method to
   * customize price range detection logic for shop-specific translation formats.
   *
   * @param amountTranslation - The translation template string to check (e.g., "{{minPrice}} - {{maxPrice}}")
   *
   * @returns True if translation contains minPrice or maxPrice placeholders, false otherwise
   *
   * @example
   * Basic usage:
   * ```typescript
   * const hasRange = helper.hasMultiVariantPriceFormat('{{minPrice}} - {{maxPrice}}');
   * // Returns: true
   * ```
   *
   * @example
   * Extend to support custom price range placeholders:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     hasMultiVariantPriceFormat(amountTranslation) {
   *       // Also check for custom "fromPrice" and "toPrice" placeholders
   *       if (amountTranslation?.includes('{{fromPrice}}') || amountTranslation?.includes('{{toPrice}}')) {
   *         return true;
   *       }
   *       return super.hasMultiVariantPriceFormat(amountTranslation);
   *     }
   *   };
   * });
   * ```
   */
  hasMultiVariantPriceFormat(amountTranslation?: string): boolean {
    if (!amountTranslation) return false;
    const regex = /\{\{(?:\s*minPrice\s*|\bminPrice\b)\}}|{{\s*maxPrice\s*|\bmaxPrice\b}}/i;
    return regex.test(amountTranslation);
  }

  /**
   * Checks if product has same min and max prices.
   *
   * Determines whether a product's price range should be collapsed to a single price
   * by comparing both the regular prices and compare-at prices. This is useful for
   * deciding whether to show "$10.00" versus "$10.00 - $15.00". Override this method
   * to implement custom price comparison logic, such as tolerance-based matching.
   *
   * @param priceMin - Minimum price in cents (e.g., 1000 for $10.00)
   * @param priceMax - Maximum price in cents (e.g., 1500 for $15.00)
   * @param compareAtPriceMin - Minimum compare-at price in cents
   * @param compareAtPriceMax - Maximum compare-at price in cents
   *
   * @returns True if both min/max regular prices and compare-at prices are identical, false otherwise
   *
   * @example
   * Basic usage:
   * ```typescript
   * const isSame = helper.isSamePrice(1000, 1000, 0, 0);
   * // Returns: true
   * ```
   *
   * @example
   * Extend to add price tolerance:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     isSamePrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax) {
   *       // Consider prices the same if within 1% tolerance
   *       const tolerance = 0.01;
   *       const priceDiff = Math.abs(Number(priceMax) - Number(priceMin));
   *       const avgPrice = (Number(priceMax) + Number(priceMin)) / 2;
   *
   *       if (priceDiff / avgPrice <= tolerance) {
   *         return true;
   *       }
   *       return super.isSamePrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax);
   *     }
   *   };
   * });
   * ```
   */
  isSamePrice(
    priceMin: number | string,
    priceMax: number | string,
    compareAtPriceMin: number | string,
    compareAtPriceMax: number | string
  ): boolean {
    return `${priceMin}-${compareAtPriceMin}` === `${priceMax}-${compareAtPriceMax}`;
  }

  /**
   * Checks if price transformation has already been applied to element.
   *
   * Prevents duplicate price formatting by checking for a data attribute flag on the DOM element.
   * This is crucial for avoiding multiple transformations when price elements are re-rendered.
   * Override this method to implement custom transformation tracking logic or use different
   * attribute naming conventions.
   *
   * @param element - DOM element to check for transformation flag
   * @param dataAttribute - Data attribute name to check (without 'data-' prefix). Defaults to 'productPrice'.
   *
   * @returns True if element has data attribute set to 'true', indicating prior transformation, false otherwise
   *
   * @example
   * Basic usage:
   * ```typescript
   * if (!helper.isPriceAlreadyTransformed(element)) {
   *   // Apply price transformation
   *   element.setAttribute('data-productPrice', 'true');
   * }
   * ```
   *
   * @example
   * Extend to use custom tracking mechanism:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     isPriceAlreadyTransformed(element, dataAttribute = 'productPrice') {
   *       // Also check for CSS class as backup indicator
   *       if (element?.classList.contains('price-transformed')) {
   *         return true;
   *       }
   *       return super.isPriceAlreadyTransformed(element, dataAttribute);
   *     }
   *   };
   * });
   * ```
   */
  isPriceAlreadyTransformed(element: Element | null, dataAttribute = 'productPrice'): boolean {
    if (!element) return false;
    return element.getAttribute(`data-${dataAttribute}`) === 'true';
  }

  /**
   * Validates if HTML contains actual price data (has digits).
   *
   * Ensures that price HTML contains numeric data before processing. This prevents
   * attempting to format empty or placeholder content. Override this method to implement
   * more sophisticated validation logic, such as checking for specific currency symbols
   * or price format patterns.
   *
   * @param html - HTML string to validate for price content
   *
   * @returns True if HTML contains at least one digit (0-9), false otherwise or if html is undefined
   *
   * @example
   * Basic usage:
   * ```typescript
   * if (helper.isValidPriceHTML('<span>$19.99</span>')) {
   *   // Process price HTML
   * }
   * ```
   *
   * @example
   * Extend to add currency symbol validation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     isValidPriceHTML(html) {
   *       // Require both digit and currency symbol
   *       if (!html) return false;
   *       const hasDigit = /\d/.test(html);
   *       const hasCurrency = /[$€£¥]/.test(html);
   *       return hasDigit && hasCurrency;
   *     }
   *   };
   * });
   * ```
   */
  isValidPriceHTML(html?: string): boolean {
    if (!html) return false;
    return /\d/.test(html);
  }

  /**
   * Replaces trailing ,00 or .00 with empty string.
   *
   * Removes trailing zero decimals from formatted prices for cleaner display
   * (e.g., "$10.00" becomes "$10"). This is commonly used when withTrailingZeros
   * is false. Override this method to customize zero decimal removal logic, such as
   * preserving certain patterns or handling different decimal formats.
   *
   * @param str - Formatted price string to process
   *
   * @returns String with trailing ,00 or .00 removed
   *
   * @example
   * Basic usage:
   * ```typescript
   * const cleaned = helper.replaceDotCommaZero('$10.00');
   * // Returns: "$10"
   * ```
   *
   * @example
   * Extend to preserve zeros for specific currencies:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterFormatHelper', (FilterFormatHelper) => {
   *   return class CustomFilterFormatHelper extends FilterFormatHelper {
   *     replaceDotCommaZero(str) {
   *       // Always show decimals for USD
   *       if (this.appService.TAEAppConfig.shop?.currency === 'USD') {
   *         return str;
   *       }
   *       return super.replaceDotCommaZero(str);
   *     }
   *   };
   * });
   * ```
   */
  replaceDotCommaZero(str: string): string {
    return str?.replace(/[.,]00$/, '');
  }

  // ============================================
  // Private Helper Methods
  // ============================================

  /**
   * Gets the format type from format string
   *
   * @param format - Format string
   * @returns Format type
   */
  private getTypeFormatMoney(format: string): string {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';
    const matches = formatString.match(placeholderRegex);
    return matches && matches.length > 1 ? matches[1] : '';
  }

  /**
   * Formats money by type with delimiters
   *
   * @param type - Format type
   * @param money - Money value
   * @returns Formatted string
   */
  private formatMoneyByType(type: string, money: number | string): string {
    const defaultOption = <T>(opt: T | undefined, def: T): T => {
      return typeof opt === 'undefined' ? def : opt;
    };

    const formatWithDelimiters = (
      number: number | string,
      precision?: number,
      thousands?: string,
      decimal?: string
    ): string => {
      if (number !== 0 && !number) return '';

      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      const numStr = parseFloat(`${number}`).toFixed(precision);
      const parts = numStr.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const cents = parts[1] ? decimal + parts[1] : '';

      return dollars + cents;
    };

    switch (type) {
      case 'amount':
        return formatWithDelimiters(money, 2);
      case 'amount_no_decimals':
        return formatWithDelimiters(money, 0);
      case 'amount_with_comma_separator':
        return formatWithDelimiters(money, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return formatWithDelimiters(money, 0, '.', ',');
      case 'amount_with_space_separator_no_comma':
        return formatWithDelimiters(money, 2);
      case 'amount_no_decimals_with_space_separator':
        return formatWithDelimiters(money, 0, ' ', '.');
      default:
        return formatWithDelimiters(money, 2);
    }
  }

  /**
   * Replaces the placeholder in format string with formatted money
   *
   * @param moneyFormat - Formatted money string
   * @param format - Format template
   * @returns Final formatted string
   */
  private compileMoneyWithConfigFormat(moneyFormat: string, format: string): string {
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';
    return formatString.replace(placeholderRegex, moneyFormat);
  }

  /**
   * Removes decimal portion from value
   *
   * @param value - Value to process
   * @param delimiter - Decimal delimiter
   * @returns Value without decimals
   */
  private removeDecimal(value: string | number, delimiter?: string): string {
    const delim = delimiter || '.';
    const reg = new RegExp('(\\' + delim + '\\d+)+', 'gi');
    return ('' + value).replace(reg, '');
  }

  /**
   * Gets decimal point character for format type
   *
   * @param format - Format type
   * @returns Decimal point character or null
   */
  private getDecimalPoint(format: string): string | null {
    switch (format) {
      case 'amount':
      case 'amount_with_space_separator_no_comma':
        return '.';
      case 'amount_with_comma_separator':
        return ',';
      case 'amount_no_decimals':
      case 'amount_no_decimals_with_comma_separator':
      case 'amount_no_decimals_with_space_separator':
      default:
        return null;
    }
  }

  /**
   * Separates integer and decimal parts of formatted money
   *
   * @param moneyFormat - Formatted money string
   * @param format - Format type
   * @param removeDecimalPoint - Whether to remove decimal point from cents
   * @returns Array of [integer part, cents part]
   */
  private separateDecimalMoney(
    moneyFormat: string,
    format: string,
    removeDecimalPoint?: boolean
  ): [string, string?] {
    const decimalPoint = this.getDecimalPoint(format);
    if (!decimalPoint) return [`${moneyFormat}`];

    const [ints, cents] = `${moneyFormat}`.split(decimalPoint);
    return cents ? [ints, removeDecimalPoint ? `${cents}` : `${decimalPoint}${cents}`] : [ints];
  }

  /**
   * Strips HTML tags from string
   *
   * @param html - HTML string
   * @returns Plain text string
   */
  private stripHtml(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  }
}
